import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap } from 'lucide-react';
import { companyInfo, logoAltUrl } from '../data/mock';
import { useSEO } from '../hooks/useSEO';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
      <Zap size={18} className="text-electric-yellow" />
      {title}
    </h2>
    <div className="text-neutral-400 leading-relaxed space-y-3">{children}</div>
  </div>
);

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO(
    'Privacy Policy | Nexline Electrical Ltd.',
    "Learn how Nexline Electrical Ltd. collects, uses, and protects your information when you visit our website or request electrical services."
  );

  const lastUpdated = 'February 2026';

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Simple top bar */}
      <header className="border-b border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-0 h-24 flex items-center justify-between">
          <Link to="/" data-testid="privacy-logo-home-link" className="flex items-center gap-3">
            <img src={logoAltUrl} alt="Nexline Electrical" className="h-14 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-home-link"
            className="flex items-center gap-2 text-neutral-400 hover:text-electric-yellow text-sm font-medium transition-colors duration-300"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[800px] mx-auto px-6 py-16 lg:py-24" data-testid="privacy-policy-page">
        <p className="text-electric-yellow text-sm font-semibold uppercase tracking-wider mb-3">Legal</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
        <p className="text-neutral-500 mb-14">Last updated: {lastUpdated}</p>

        <Section title="Overview">
          <p>
            {companyInfo.name} ("we", "our", or "us") respects your privacy. This Privacy Policy explains
            how we collect, use, and protect information when you visit our website
            ({companyInfo.name === 'Nexline Electrical Ltd.' ? 'nexlineltd.ca' : ''}) or contact us about our
            residential and commercial electrical services.
          </p>
        </Section>

        <Section title="Information We Collect">
          <p>We may collect the following types of information:</p>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>
              <span className="text-white font-medium">Contact information</span> you voluntarily provide when
              you call, email, or message us to request a quote (e.g., name, phone number, email address,
              service address).
            </li>
            <li>
              <span className="text-white font-medium">Usage data</span> such as pages visited, browser type,
              and device information, collected automatically via analytics tools to help us improve the
              website experience.
            </li>
            <li>
              <span className="text-white font-medium">Social media interactions</span> if you engage with our
              embedded Instagram content or follow us on social platforms.
            </li>
          </ul>
        </Section>

        <Section title="How We Use Your Information">
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>To respond to quote requests and service inquiries.</li>
            <li>To communicate about scheduling, estimates, and completed work.</li>
            <li>To improve our website's performance, content, and user experience.</li>
            <li>To comply with legal and regulatory obligations in Canada.</li>
          </ul>
        </Section>

        <Section title="Cookies & Analytics">
          <p>
            Our website may use cookies and third-party analytics tools to understand how visitors use our
            site. This helps us improve performance and content. You can disable cookies through your browser
            settings; however, some site features may not function as intended.
          </p>
        </Section>

        <Section title="Third-Party Content">
          <p>
            Our Portfolio section embeds content from Instagram. When you view these embeds, Instagram
            (Meta Platforms, Inc.) may collect data according to its own privacy policy. We do not control or
            take responsibility for third-party data practices.
          </p>
        </Section>

        <Section title="Data Sharing">
          <p>
            We do not sell, rent, or trade your personal information. We may share information with trusted
            service providers who assist us in operating our business (e.g., scheduling or communication
            tools), and only to the extent necessary for them to perform their services.
          </p>
        </Section>

        <Section title="Data Security">
          <p>
            We take reasonable administrative and technical measures to protect the information you share with
            us. However, no method of transmission over the internet is 100% secure.
          </p>
        </Section>

        <Section title="Your Rights">
          <p>
            You may contact us at any time to request access to, correction of, or deletion of your personal
            information that we hold, subject to applicable Canadian privacy laws (including PIPEDA).
          </p>
        </Section>

        <Section title="Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices or for
            legal reasons. The "Last updated" date above will reflect the most recent revision.
          </p>
        </Section>

        <Section title="Contact Us">
          <p>If you have any questions about this Privacy Policy, please contact us:</p>
          <ul className="space-y-1 pl-2">
            <li>
              Email:{' '}
              <a href={`mailto:${companyInfo.email}`} className="text-electric-yellow hover:underline">
                {companyInfo.email}
              </a>
            </li>
            <li>
              Phone:{' '}
              <a href={`tel:${companyInfo.phone}`} className="text-electric-yellow hover:underline">
                {companyInfo.phone}
              </a>
            </li>
            <li>Coverage: {companyInfo.coverage}</li>
          </ul>
        </Section>

        <div className="mt-16 pt-8 border-t border-white/5">
          <Link
            to="/"
            data-testid="privacy-back-home-button"
            className="inline-flex items-center gap-2 bg-electric-yellow text-black px-6 py-3 text-base font-medium hover:bg-transparent hover:text-electric-yellow border border-electric-yellow transition-all duration-400"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
